import { PageStatus } from '@/types/store';
import type { FunnelPageItem, FunnelRoutingEvent } from '../types';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';

/**
 * Semantic role a page plays inside the funnel graph. Drives the role badge
 * shown next to each step row in the sidebar / dashboard so creators can see
 * at a glance which page is an upsell / downsell of which other page.
 */
export type FunnelPageRelationshipKind = 'lead_capture' | 'main_offer' | 'upsell' | 'downsell' | 'follow_up';

export interface FunnelPageRelationship {
    kind: FunnelPageRelationshipKind;
    /** Step number this page branches off of (for upsells / downsells). */
    fromStep?: number;
}

/**
 * Classify each page by its role in the branching graph:
 *   • first visible page (lowest order, not hidden) → `lead_capture`
 *   • second visible page → `main_offer`
 *   • any page that is the `on_purchase` target of an earlier page → `upsell`
 *   • any page that is the `on_skip` target of an earlier page → `downsell`
 *   • anything else reachable via linear fallback → `follow_up`
 *
 * Hidden (DRAFT) pages never claim the entry/main slots so the labelling
 * matches what visitors actually see — when the order-1 page is hidden, the
 * next visible page becomes the entry page.
 *
 * Explicit `on_purchase` / `on_skip` routing takes precedence over positional
 * fallbacks, so a page is only labelled "Following Offer" when nobody routes to
 * it explicitly.
 */
export function getFunnelPageRelationships(pages: FunnelPageItem[]): Map<number, FunnelPageRelationship> {
    const result = new Map<number, FunnelPageRelationship>();
    const saved = pages.filter(p => p.page_id !== DRAFT_FUNNEL_PAGE_ID).sort((a, b) => a.order - b.order);
    if (saved.length === 0) return result;

    // Default everything to follow_up, then promote the first two *visible*
    // pages to lead_capture / main_offer. Hidden pages stay as follow_up so
    // they don't steal the entry-page label from the next visible step.
    saved.forEach(page => result.set(page.page_id, { kind: 'follow_up' }));

    const visible = saved.filter(p => p.status !== PageStatus.DRAFT);
    if (visible[0]) result.set(visible[0].page_id, { kind: 'lead_capture' });
    if (visible[1]) result.set(visible[1].page_id, { kind: 'main_offer' });

    // Explicit routing edges override the positional fallback for steps 3+.
    saved.forEach(source => {
        if (!source.routing) return;
        const applyEdge = (event: FunnelRoutingEvent, kind: 'upsell' | 'downsell') => {
            const target = source.routing?.[event];
            if (!target) return;
            const existing = result.get(target.page_id);
            if (!existing || existing.kind === 'lead_capture' || existing.kind === 'main_offer') return;
            result.set(target.page_id, { kind, fromStep: source.order });
        };
        applyEdge('on_purchase', 'upsell');
        applyEdge('on_skip', 'downsell');
    });

    return result;
}

export type FunnelPageBranchTag = { label: string; colorClass: string };

/** Short role label shown as the top-level row subtitle. */
export function getFunnelPageRelationshipLabel(relationship: FunnelPageRelationship | undefined): string {
    switch (relationship?.kind) {
        case 'lead_capture':
            return 'Entry Page';
        case 'main_offer':
            return 'Subsequent Offer';
        case 'upsell':
            return 'Upsell';
        case 'downsell':
            return 'Downsell';
        case 'follow_up':
        default:
            return 'Following Offer';
    }
}

/** Colored tag rendered on branch (upsell/downsell) rows. */
export function getFunnelPageBranchTag(relationship: FunnelPageRelationship | undefined): FunnelPageBranchTag {
    if (relationship?.kind === 'upsell') return { label: 'Upsell', colorClass: 'text-positive-fg-positive-bold' };
    if (relationship?.kind === 'downsell') return { label: 'Downsell', colorClass: 'text-warning-fg-warning-bold' };
    return { label: getFunnelPageRelationshipLabel(relationship), colorClass: 'text-gray' };
}
